import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Typography,
  Divider,
  IconButton,
} from '@mui/material'
import { CloudUpload, Delete } from '@mui/icons-material'
import { Modal, Button } from '@components/common'
import api from '@services/api'

const STATUS_OPTIONS = [
  { value: 'operational', label: 'Operational' },
  { value: 'under_maintenance', label: 'Under Maintenance' },
  { value: 'out_for_repair', label: 'Out for External Repair' },
  { value: 'out_of_service', label: 'Out of Service' },
  { value: 'retired', label: 'Retired' },
  { value: 'pending_installation', label: 'Pending Installation' },
  { value: 'condemned', label: 'Condemned/Dead' },
]

const CRITICALITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

const initialFormData = {
  name: '',
  description: '',
  category_id: '',
  department_id: '',
  manufacturer_id: '',
  model_number: '',
  serial_number: '',
  location_id: '',
  room_number: '',
  status: 'operational',
  criticality: 'medium',
  purchase_date: '',
  purchase_cost: '',
  warranty_expiry: '',
  expected_life_years: '',
  // New fields from client requirements
  manufacturing_date: '',
  country_of_origin: '',
  supplier_id: '',
  received_by_id: '',
  received_date: '',
  delivered_by: '',
  notes: '',
}

const AssetForm = ({ open, onClose, asset, onSuccess }) => {
  const [formData, setFormData] = useState(initialFormData)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [categories, setCategories] = useState([])
  const [departments, setDepartments] = useState([])
  const [manufacturers, setManufacturers] = useState([])
  const [locations, setLocations] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [users, setUsers] = useState([])

  // Image upload state
  const [imageFiles, setImageFiles] = useState({ image: null, image_2: null, image_3: null, qr_code: null })
  const [imagePreviews, setImagePreviews] = useState({ image: null, image_2: null, image_3: null, qr_code: null })

  const isEditing = Boolean(asset)

  useEffect(() => {
    if (open) {
      fetchOptions()
      if (asset) {
        setFormData({
          name: asset.name || '',
          description: asset.description || '',
          category_id: asset.category?.id || '',
          department_id: asset.department || '',
          manufacturer_id: asset.manufacturer?.id || '',
          model_number: asset.model_number || '',
          serial_number: asset.serial_number || '',
          location_id: asset.location?.id || '',
          room_number: asset.room_number || '',
          status: asset.status || 'operational',
          criticality: asset.criticality || 'medium',
          purchase_date: asset.purchase_date || '',
          purchase_cost: asset.purchase_cost || '',
          warranty_expiry: asset.warranty_expiry || '',
          expected_life_years: asset.expected_life_years || '',
          // New fields
          manufacturing_date: asset.manufacturing_date || '',
          country_of_origin: asset.country_of_origin || '',
          supplier_id: asset.supplier || '',
          received_by_id: asset.received_by || '',
          received_date: asset.received_date || '',
          delivered_by: asset.delivered_by || '',
          notes: asset.notes || '',
        })
        // Set existing image previews
        setImagePreviews({
          image: asset.image || null,
          image_2: asset.image_2 || null,
          image_3: asset.image_3 || null,
          qr_code: asset.qr_code || null,
        })
        setImageFiles({ image: null, image_2: null, image_3: null, qr_code: null })
      } else {
        setFormData(initialFormData)
        setImagePreviews({ image: null, image_2: null, image_3: null, qr_code: null })
        setImageFiles({ image: null, image_2: null, image_3: null, qr_code: null })
      }
      setErrors({})
    }
  }, [open, asset])

  const fetchOptions = async () => {
    try {
      const [catRes, deptRes, mfgRes, locRes, supplierRes, usersRes] = await Promise.all([
        api.get('/assets/categories/'),
        api.get('/common/departments/'),
        api.get('/assets/manufacturers/'),
        api.get('/assets/locations/'),
        api.get('/vendors/'),
        api.get('/common/users/'),
      ])
      setCategories(catRes.data.results || catRes.data)
      setDepartments(deptRes.data.results || deptRes.data)
      setManufacturers(mfgRes.data.results || mfgRes.data)
      setLocations(locRes.data.results || locRes.data)
      setSuppliers(supplierRes.data.results || supplierRes.data)
      setUsers(usersRes.data.results || usersRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleImageChange = (e, fieldName) => {
    const file = e.target.files[0]
    if (file) {
      setImageFiles(prev => ({ ...prev, [fieldName]: file }))
      setImagePreviews(prev => ({ ...prev, [fieldName]: URL.createObjectURL(file) }))
    }
  }

  const handleRemoveImage = (fieldName) => {
    setImageFiles(prev => ({ ...prev, [fieldName]: null }))
    setImagePreviews(prev => ({ ...prev, [fieldName]: null }))
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.category_id) newErrors.category_id = 'Category is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setLoading(true)
    try {
      // Use FormData for multipart/form-data (required for image uploads)
      const submitData = new FormData()

      // Add all form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          // Handle field name mapping
          if (key === 'supplier_id') {
            submitData.append('supplier', value)
          } else if (key === 'received_by_id') {
            submitData.append('received_by', value)
          } else if (key === 'department_id') {
            submitData.append('department', value)
          } else {
            submitData.append(key, value)
          }
        }
      })

      // Add image files if selected
      if (imageFiles.image) {
        submitData.append('image', imageFiles.image)
      }
      if (imageFiles.image_2) {
        submitData.append('image_2', imageFiles.image_2)
      }
      if (imageFiles.image_3) {
        submitData.append('image_3', imageFiles.image_3)
      }
      if (imageFiles.qr_code) {
        submitData.append('qr_code', imageFiles.qr_code)
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }

      if (isEditing) {
        await api.patch(`/assets/assets/${asset.id}/`, submitData, config)
      } else {
        await api.post('/assets/assets/', submitData, config)
      }

      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error saving asset:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Asset' : 'Add New Asset'}
      maxWidth="md"
      actions={
        <>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Asset Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={Boolean(errors.category_id)} required>
            <InputLabel>Category</InputLabel>
            <Select
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              label="Category"
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Department</InputLabel>
            <Select
              name="department_id"
              value={formData.department_id}
              onChange={handleChange}
              label="Department"
            >
              <MenuItem value="">None</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Manufacturer</InputLabel>
            <Select
              name="manufacturer_id"
              value={formData.manufacturer_id}
              onChange={handleChange}
              label="Manufacturer"
            >
              <MenuItem value="">None</MenuItem>
              {manufacturers.map((mfg) => (
                <MenuItem key={mfg.id} value={mfg.id}>
                  {mfg.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Model Number"
            name="model_number"
            value={formData.model_number}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Serial Number"
            name="serial_number"
            value={formData.serial_number}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Location</InputLabel>
            <Select
              name="location_id"
              value={formData.location_id}
              onChange={handleChange}
              label="Location"
            >
              <MenuItem value="">None</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.name} {loc.building && `- ${loc.building}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Room Number"
            name="room_number"
            value={formData.room_number}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleChange}
              label="Status"
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Criticality</InputLabel>
            <Select
              name="criticality"
              value={formData.criticality}
              onChange={handleChange}
              label="Criticality"
            >
              {CRITICALITY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Purchase Date"
            name="purchase_date"
            type="date"
            value={formData.purchase_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Purchase Cost"
            name="purchase_cost"
            type="number"
            value={formData.purchase_cost}
            onChange={handleChange}
            InputProps={{
              startAdornment: <InputAdornment position="start">UGX</InputAdornment>,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Warranty Expiry"
            name="warranty_expiry"
            type="date"
            value={formData.warranty_expiry}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Expected Life (Years)"
            name="expected_life_years"
            type="number"
            value={formData.expected_life_years}
            onChange={handleChange}
          />
        </Grid>

        {/* Manufacturing & Origin Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Manufacturing & Origin
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Manufacturing Date"
            name="manufacturing_date"
            type="date"
            value={formData.manufacturing_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Country of Origin"
            name="country_of_origin"
            value={formData.country_of_origin}
            onChange={handleChange}
          />
        </Grid>

        {/* Receiving Information Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Receiving Information
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Supplier</InputLabel>
            <Select
              name="supplier_id"
              value={formData.supplier_id}
              onChange={handleChange}
              label="Supplier"
            >
              <MenuItem value="">None</MenuItem>
              {suppliers.map((sup) => (
                <MenuItem key={sup.id} value={sup.id}>
                  {sup.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Delivered By"
            name="delivered_by"
            value={formData.delivered_by}
            onChange={handleChange}
            placeholder="Name of delivery person"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Received By</InputLabel>
            <Select
              name="received_by_id"
              value={formData.received_by_id}
              onChange={handleChange}
              label="Received By"
            >
              <MenuItem value="">None</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Received Date"
            name="received_date"
            type="date"
            value={formData.received_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Images Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Asset Images
          </Typography>
        </Grid>

        {['image', 'image_2', 'image_3'].map((fieldName, index) => (
          <Grid item xs={12} md={4} key={fieldName}>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                position: 'relative',
                minHeight: 140,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              {imagePreviews[fieldName] ? (
                <>
                  <Box
                    component="img"
                    src={imagePreviews[fieldName]}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 100,
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveImage(fieldName)}
                    sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper' }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <CloudUpload sx={{ fontSize: 32, color: 'text.secondary', mb: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {index === 0 ? 'Primary Image' : `Image ${index + 1}`}
                  </Typography>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e, fieldName)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                }}
              />
            </Box>
          </Grid>
        ))}

        {/* QR Code Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            QR Code (Optional - will be auto-generated if not provided)
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              position: 'relative',
              minHeight: 140,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            {imagePreviews.qr_code ? (
              <>
                <Box
                  component="img"
                  src={imagePreviews.qr_code}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 100,
                    objectFit: 'contain',
                    borderRadius: 1,
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveImage('qr_code')}
                  sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper' }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </>
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 32, color: 'text.secondary', mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  Upload QR Code
                </Typography>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e, 'qr_code')}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default AssetForm
